/**
 * O erro da TELA chegando ao painel de observabilidade.
 *
 * Tela branca no computador da recepção é o defeito mais caro de descobrir: quem
 * vê não sabe descrever, e quem sabe consertar não vê. Aqui o navegador conta
 * sozinho — `window.onerror`, promessa rejeitada sem dono e o erro que o React
 * pegou (com a pilha de COMPONENTES, que é o que diz qual tela quebrou) — para
 * `POST /api/telemetria/erro-da-tela`, que agrupa e manda o e-mail.
 *
 * Três travas contra enxurrada, porque erro em laço de render dispara a cada
 * quadro: o mesmo erro não sai de novo em 30s, no máximo 20 por página aberta, e
 * a falha do próprio envio é engolida (reportar que não deu para reportar é o
 * laço de novo).
 */

const MESMO_ERRO_MS = 30_000;
const TETO_POR_PAGINA = 20;

/** O relato de um erro qualquer, no formato da rota. Pura. */
export function montarRelato(erro, extra = {}) {
  const e = erro instanceof Error ? erro : null;
  const mensagem = e
    ? e.message
    : typeof erro === "string"
      ? erro
      : erro?.message
        ? String(erro.message)
        : String(erro ?? "erro sem mensagem");
  return {
    mensagem: (mensagem || "erro sem mensagem").slice(0, 4000),
    tipo: (e?.name || extra.tipo || "Error").slice(0, 120),
    stack: (e?.stack || extra.stack || "").slice(0, 16000) || undefined,
    url: extra.url,
    componente: extra.componente ? String(extra.componente).slice(0, 4000) : undefined,
  };
}

/** A porta de saída: decide se ESTE relato sai agora. Com estado, testável. */
export function criarPorta({ agora = () => Date.now(), teto = TETO_POR_PAGINA, janela = MESMO_ERRO_MS } = {}) {
  const vistos = new Map();
  let enviados = 0;
  return (relato) => {
    if (enviados >= teto) return false;
    const chave = `${relato.tipo}|${relato.mensagem}|${(relato.stack ?? "").split("\n")[1] ?? ""}`;
    const antes = vistos.get(chave);
    if (antes !== undefined && agora() - antes < janela) return false;
    vistos.set(chave, agora());
    enviados++;
    return true;
  };
}

const csrf = () =>
  document.cookie
    .split("; ")
    .find((c) => c.startsWith("crm_csrf="))
    ?.slice("crm_csrf=".length) ?? "";

function enviarDeVerdade(relato) {
  /* `fetch` cru e não `api.mandar`: o `mandar` trata 401 como sessão caída e
     manda a pessoa para a porta — um erro na tela de login faria isso em laço. */
  fetch("/api/telemetria/erro-da-tela", {
    method: "POST",
    credentials: "same-origin",
    keepalive: true,
    headers: { "content-type": "application/json", "x-crm-csrf": decodeURIComponent(csrf()) },
    body: JSON.stringify(relato),
  }).catch(() => undefined);
}

const porta = criarPorta();
const local = () => (typeof location !== "undefined" ? `${location.pathname}${location.search}` : undefined);

export function relatar(erro, extra = {}, enviar = enviarDeVerdade, passa = porta) {
  try {
    const relato = montarRelato(erro, { url: local(), ...extra });
    if (passa(relato)) enviar(relato);
  } catch {
    /* relatar nunca quebra a tela que já está quebrando */
  }
}

/** Os ouvintes globais. Chamado uma vez, na importação deste arquivo pelo `main.jsx`. */
export function ligarOuvinteDeErros(alvo = typeof window !== "undefined" ? window : null) {
  if (!alvo || alvo.__ouvinteDeErros) return;
  alvo.__ouvinteDeErros = true;
  alvo.addEventListener("error", (ev) => {
    /* Recurso que não carregou (imagem, script) chega aqui sem `error`: não é
       exceção de código, e a foto de perfil que expirou não é defeito. */
    if (!ev.error && !ev.message) return;
    relatar(ev.error ?? ev.message, { stack: ev.filename ? `em ${ev.filename}:${ev.lineno}:${ev.colno}` : undefined });
  });
  alvo.addEventListener("unhandledrejection", (ev) => {
    const motivo = ev.reason;
    /* Pedido cancelado pela própria tela (troca de caso no meio da carga) não é erro. */
    if (motivo?.name === "AbortError") return;
    /* Recusa da casa (`{erro}` em português, 400/403/404) é resposta, não defeito. */
    if (motivo?.daCasa) return;
    relatar(motivo, { tipo: "unhandledrejection" });
  });
}

/**
 * Os ganchos do React 19 no `createRoot`: o erro de render chega com a pilha de
 * componentes. Continuam escrevendo no console, como o padrão do React fazia.
 */
export const ouvintesDoReact = {
  onUncaughtError: (erro, info) => {
    console.error(erro);
    relatar(erro, { componente: info?.componentStack });
  },
  onCaughtError: (erro, info) => {
    console.error(erro);
    relatar(erro, { componente: info?.componentStack });
  },
};

ligarOuvinteDeErros();
